// The built-in skills in a person's words: a plain name, one line on what
// it does, what it is for (the Skills screens group by this), and an icon.
// A skill's own description is written for the agent ("Use when ...",
// command names, addresses); this is what the operator reads. A built-in
// that is not listed here (a new one, or one added for one workspace)
// falls back to its own name and the first sentence of its description,
// under "Other". Pure (builtIns.selftest.ts).

import type { LucideIcon } from "lucide-react";
import {
  Activity,
  AppWindow,
  Bot,
  Brain,
  CalendarClock,
  ChartLine,
  Code,
  Database,
  FileChartColumn,
  FileSpreadsheet,
  FileText,
  Globe,
  KeyRound,
  Landmark,
  Library,
  ListChecks,
  ListTodo,
  LogIn,
  Mail,
  Mic,
  Monitor,
  MousePointerClick,
  Newspaper,
  Palette,
  Plug,
  Presentation,
  RefreshCw,
  Repeat,
  ScanEye,
  Send,
  Server,
  Smile,
  Sparkles,
  Sunrise,
  Ticket,
  TrendingUp,
  UserSearch,
  Users,
  Video,
  Workflow,
  Zap,
} from "lucide-react";

export type Purpose = "mail" | "documents" | "research" | "data" | "automation" | "computer" | "other" | "background";

/** What a group of built-ins is for, in the order the screens show them. */
export const PURPOSES: readonly { key: Purpose; title: string }[] = [
  { key: "mail", title: "Mail, diary and meetings" },
  { key: "documents", title: "Documents and reports" },
  { key: "research", title: "Research and media" },
  { key: "data", title: "Data and markets" },
  { key: "automation", title: "Automation and building" },
  { key: "computer", title: "Browser and computer" },
  { key: "other", title: "Other" },
  { key: "background", title: "Behind the scenes" },
];

export interface BuiltIn {
  name: string;
  line: string;
  purpose: Purpose;
  icon: LucideIcon;
}

const B = (name: string, line: string, purpose: Purpose, icon: LucideIcon): BuiltIn => ({ name, line, purpose, icon });

const BUILT_INS: Record<string, BuiltIn> = {
  "lattice-google-workspace": B("Mail, calendar and files", "Reads and sends mail, keeps the diary and works with shared files", "mail", Mail),
  "lattice-meetings": B("Booking links and follow-ups", "Makes links for people to book time, and follows up after meetings", "mail", CalendarClock),
  "lattice-bookings": B("Bookings", "Books tables, tickets and appointments for you", "mail", Ticket),
  "lattice-briefing": B("Daily briefing", "One morning message: today, decisions, open actions and mail worth a look", "mail", Sunrise),
  "lattice-todos": B("To-dos", "Reads and adds to your to-do list", "mail", ListTodo),
  "lattice-routines": B("Reminders and routines", "Sets up reminders and work that repeats", "mail", Repeat),

  "lattice-docs": B("Edit documents", "Edits PDF, Word, Excel and PowerPoint files so they look untouched", "documents", FileText),
  "lattice-pdf-report": B("PDF reports", "Writes reports and briefs as polished PDFs, with charts and diagrams", "documents", FileChartColumn),
  "lattice-deck": B("Slide decks", "Makes slide decks that look designed", "documents", Presentation),
  "lattice-styles": B("House styles", "Learns a brand's look from an example and uses it for new documents", "documents", Palette),
  "excel-xlsx": B("Spreadsheets", "Builds and edits Excel workbooks", "documents", FileSpreadsheet),
  "word-docx": B("Word documents", "Builds and edits Word documents", "documents", FileText),
  "powerpoint-pptx": B("PowerPoint files", "Builds and edits PowerPoint files", "documents", Presentation),

  "lattice-knowledge": B("Knowledge libraries", "Builds and searches libraries of books, papers and notes", "research", Library),
  "lattice-people": B("People and companies", "Researches people and companies, and maps who knows whom", "research", Users),
  "lattice-gather": B("Public figures", "Gathers what a public figure has said and written, from talks to articles", "research", UserSearch),
  "lattice-public-search": B("Web lookups", "Looks things up on the web in its own browser", "research", Globe),
  "lattice-news": B("News", "Searches summarised financial and general news", "research", Newspaper),
  "lattice-media": B("Videos and podcasts", "Transcribes videos and podcasts, and follows channels on a topic", "research", Video),
  "lattice-podcast": B("Narration and podcasts", "Turns text into narration or a two-voice podcast, in a chosen voice", "research", Mic),

  "lattice-data": B("Your data", "Queries the databases and files you connected", "data", Database),
  "lattice-market-data": B("Market data", "Prices, history, fundamentals and screens", "data", ChartLine),
  "lattice-macro": B("Economic indicators", "Growth, inflation, jobs and debt by country, back to 1960", "data", Landmark),
  analyst_momentum_us: B("Analyst momentum (US)", "US sectors ranked by analysts' earnings revisions", "data", TrendingUp),

  "lattice-workflows": B("Workflows", "Drafts, runs and schedules saved workflows and long jobs", "automation", Workflow),
  "lattice-tasks": B("Tasks", "Hands longer work to itself, a colleague or a subagent, and keeps track", "automation", ListChecks),
  "lattice-subagents": B("Subagents", "Creates and looks after specialist subagents", "automation", Bot),
  "lattice-deliver": B("Deliveries", "Sends a finished workflow's results where you asked", "automation", Send),
  "lattice-cc": B("Coding sessions", "Starts, steers and checks on coding sessions", "automation", Code),
  "lattice-deploy": B("Apps", "Builds and runs apps, dashboards and tools for the workspace", "automation", AppWindow),
  "lattice-machines": B("Your machines", "Runs commands on the machines you connected", "automation", Server),
  "lattice-connections": B("Connecting services", "Connects a product or service you name in chat", "automation", Plug),
  "lattice-integrations": B("Tools and keys", "Adds a tool server or a key you give it", "automation", KeyRound),

  "lattice-browser-control": B("Browser", "Uses its own browser, including inboxes you delegated", "computer", MousePointerClick),
  "lattice-desktop-control": B("Desktop", "Clicks and types on its own computer desktop", "computer", Monitor),
  "lattice-login-assist": B("Sign-in help", "Sends you a one-time link when a site needs you to sign in", "computer", LogIn),

  "lattice-memory": B("Memory", "Writes down what it should remember", "background", Brain),
  "lattice-activity": B("Activity log", "Notes finished work in the Activity feed", "background", Activity),
  "lattice-personality": B("Personality", "Changes how it talks to you when you ask", "background", Smile),
  "lattice-skills": B("Making skills", "Writes a new skill when you ask, and installs it on your yes", "background", Sparkles),
  "lattice-vision": B("Image checks", "Checks a screenshot, chart or page against what it should show", "background", ScanEye),
  proactivity: B("Proactivity", "Spots likely next steps and keeps work moving", "background", Zap),
  "self-improving": B("Learning from mistakes", "Learns from corrections, so repeat work gets better", "background", RefreshCw),
};

/**
 * The built-ins that are how Connections work underneath (adding one, its
 * data, its keys, the machines): where Connections is on they stay
 * installed and agents keep using them, but the Skills screens do not list
 * them as skills (what agents reach is in Settings › Connections).
 */
export const CONNECTIONS_UNDERNEATH: readonly string[] = ["lattice-data", "lattice-connections", "lattice-integrations", "lattice-machines"];

/** What a subagent's page and pickers never show as something it can use: the above, and how a run reports itself (lattice-subagents). */
export const PLUMBING: readonly string[] = [...CONNECTIONS_UNDERNEATH, "lattice-subagents"];

/** The built-in's plain words, or null for one not listed (the caller falls back to its own). */
export function builtIn(slug: string): BuiltIn | null {
  return BUILT_INS[slug] ?? null;
}

/** Every slug with plain words (the selftest checks each is well formed). */
export function knownBuiltIns(): string[] {
  return Object.keys(BUILT_INS);
}

/** The purpose a built-in is listed under ("other" when it is not known). */
export function purposeOf(slug: string): Purpose {
  return BUILT_INS[slug]?.purpose ?? "other";
}

const ADDRESS = /(?:\s+(?:at|on|via|from)\s+)?(?:https?:\/\/\S+|\b\d{1,3}(?:\.\d{1,3}){3}(?::\d+)?\b)/gi;

/**
 * One line from a description written for an agent: its first sentence,
 * without backticks, web or network addresses, or a trailing "Use when ...".
 */
export function plainLine(description: string | null | undefined): string {
  let text = (description ?? "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  text = text.replace(/`([^`]*)`/g, "$1").replace(ADDRESS, "").replace(/\s+([,.;:])/g, "$1");
  // The first sentence, when it says enough on its own.
  const end = /[.!?](?:\s|$)/.exec(text);
  if (end && end.index >= 24) text = text.slice(0, end.index);
  text = text.replace(/\s*(?:[-–—]\s*)?\bUse (?:it )?(?:when|whenever|for)\b.*$/i, "").trim();
  return text.replace(/[.;:,]+$/, "");
}
